import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { compareAccommodationsByComfort } from 'Utils/proposal/proposalUtils';
import { CEFALU_RESORT } from 'Utils/proposal/constants';
import { VILLETA_ROOM_REGEX } from 'Utils/accommodation/accommodation.constants';

type DiscountDetail = {
  value: number;
  description: string;
};

const getDiscountValueFromProposal = (proposal: Proposal) =>
  proposal.price.discounts.find(({ currentProposalCode }) => !!currentProposalCode?.value)
    ?.currentProposalCode?.value;

const getUniqDiscountProposals = (proposals: Array<Proposal>): Array<number> =>
  uniq(proposals.map((proposal) => getDiscountValueFromProposal(proposal)).filter(Boolean));

const getDiscountDetailFromProposal = (proposal: Proposal): DiscountDetail | null => {
  const discount = proposal.price.discounts.find(
    ({ currentProposalCode }) => !!currentProposalCode?.value,
  );
  if (!discount?.currentProposalCode?.value) {
    return null;
  }
  return {
    value: discount.currentProposalCode.value,
    description: discount.currentProposalCode.description || '',
  };
};

const getUniqDiscountDetails = (proposals: Array<Proposal>): Array<DiscountDetail> =>
  uniqBy(
    proposals.map((proposal) => getDiscountDetailFromProposal(proposal)).filter(Boolean),
    'value',
  );

function getAccommodationTitle(proposal: Proposal, product: Product): string {
  const accommodations = [...proposal.accommodations]
    .sort(compareAccommodationsByComfort)
    .reverse();
  const category = accommodations?.[0]?.category || '';
  const comfort = accommodations?.[0]?.comfortType || '';
  const label = accommodations?.[0]?.label || '';
  const isVillettaRoom =
    product.id.toLowerCase() === CEFALU_RESORT.toLowerCase() &&
    VILLETA_ROOM_REGEX.test(accommodations?.[0]?.id);
  return isVillettaRoom ? label || comfort || category : comfort || category;
}

function getExclusiveCollectionInAccommodations(proposal: Proposal, product: Product): boolean {
  const indexOfExclusiveCollectionRoom = proposal?.accommodations.findIndex(
    (accommodation) => accommodation.exclusiveCollection,
  );
  return indexOfExclusiveCollectionRoom !== -1 && product?.comfort === 4;
}

const isSuperiorComfortAccommodation = (accommodations: Array<Accommodation>) =>
  accommodations?.[0]?.categories?.every(({ id }) => id === '000017' || id === '000022');

export {
  getAccommodationTitle,
  getUniqDiscountProposals,
  getUniqDiscountDetails,
  getDiscountValueFromProposal,
  getExclusiveCollectionInAccommodations,
  isSuperiorComfortAccommodation,
};
